import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useItemPanel } from '@/components/item-panel';
import { Item } from './types';
import { ITEM_PARAM } from './url-state';

/** True when the user asked the OS to reduce motion — deep-link scrolls then jump instead of animate. */
export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Ids of an item's ancestors (immediate parent → root), walked via parent_id. Cycle-safe. */
export const ancestorIds = (id: string, byId: Map<string, Item>): Set<string> => {
  const out = new Set<string>();
  let cur = byId.get(id)?.parent_id ?? null;
  for (let i = 0; cur && byId.has(cur) && !out.has(cur) && i < 1000; i++) {
    out.add(cur);
    cur = byId.get(cur)!.parent_id ?? null;
  }
  return out;
};

type Opts = {
  /** Center the item in this view. Defaults to scrolling the item's row/card element to the middle;
   *  the Timeline overrides it to also center the item's date on the horizontal axis. `el` is the
   *  element carrying `data-item-id`. */
  scrollToItem?: (id: string, el: HTMLElement) => void;
  /** Reveal a folded item before centering it (expand its collapsed ancestors), so a link to a nested
   *  item lands on the item itself rather than the collapsed parent it was tucked inside. */
  revealItem?: (id: string) => void;
};

/**
 * Two-way sync between the open details panel and a shareable `?item=<KEY>` query param, plus a
 * scroll-to-center when a link opens an item. Used by the Table / Kanban / Timeline views — each holds
 * the board's items, which is what maps the friendly KEY in the URL to the item id the panel wants (and
 * back). The item id never appears in the URL; the human key does, matching the friendly routes.
 *
 *   URL → panel: on load / navigation, resolve `?item=<key>`, open the panel on it, reveal + center it.
 *   panel → URL: mirror the open item's key into `?item` (history-replace), clearing it on close.
 *
 * Clicking a row opens the panel directly, so the mirror just reflects it into the URL — no scroll.
 *
 * Guards worth knowing:
 *  - `readyRef` lets the initial URL win the first reconcile, so the mirror never strips a fresh
 *    deep-link before it's been opened.
 *  - `expectedKeyRef` bridges the render gap between openItem and the panel state actually reflecting
 *    it, so the mirror doesn't briefly delete a just-opened item.
 *  - `pendingKeyRef` remembers a `?item` whose key isn't in THIS board's items yet (a back/forward onto
 *    a reused view before its items refetch — the route `/:slug` component is not remounted across a
 *    board switch), so the reconcile retries once the items load rather than stripping the param.
 *
 * Each view marks its row/card root with `data-item-id={item.id}`, the scroll target (it appears only
 * once the item is on screen — after its ancestors are revealed). UrlSync preserves this param; it
 * does not own it.
 */
export function useItemUrlSync(items: Item[], opts: Opts = {}): void {
  const { scrollToItem: customScroll, revealItem } = opts;
  const panel = useItemPanel();
  const [params, setParams] = useSearchParams();
  const itemParam = params.get(ITEM_PARAM);

  const byKey = useMemo(() => new Map(items.map((i) => [(i.key || '').toLowerCase(), i])), [items]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  // Read the latest values off refs so the sync effects stay keyed on the URL / selection rather than
  // re-running on every re-render of the item set.
  const selectedIdRef = useRef(panel.selectedId);
  selectedIdRef.current = panel.selectedId;
  const openItemRef = useRef(panel.openItem);
  openItemRef.current = panel.openItem;
  const revealRef = useRef(revealItem);
  revealRef.current = revealItem;

  const readyRef = useRef(false); // has the initial URL had its chance to open the panel?
  const expectedKeyRef = useRef<string | null>(null); // key just asked to open, awaiting the state flush
  const pendingKeyRef = useRef<string | null>(null); // a ?item key this board's items haven't loaded yet
  const scrollTargetRef = useRef<string | null>(null); // item id to center once its row has painted

  // Open the item a `?item=<key>` names (if any) and queue it to be revealed + centered.
  const openFromParam = useCallback(
    (raw: string | null) => {
      const key = raw ? raw.toLowerCase() : '';
      if (!key) {
        pendingKeyRef.current = null;
        return;
      }
      const target = byKey.get(key);
      if (!target) {
        // The key isn't in this board's items yet — a back/forward onto a reused view before its items
        // refetch, or a board still loading. Remember it; the reconcile retries when byKey grows.
        pendingKeyRef.current = key;
        return;
      }
      pendingKeyRef.current = null;
      if (target.id === selectedIdRef.current) return;
      expectedKeyRef.current = key;
      scrollTargetRef.current = target.id;
      revealRef.current?.(target.id);
      openItemRef.current(target.id);
    },
    [byKey],
  );

  // URL → panel. Fires on a real `?item` change (an in-app link, or browser back/forward) and when this
  // board's items change while a `?item` is still pending (its key wasn't loaded yet — the reused-view
  // case). Gated so a mere item-set change never REOPENS an item the user just closed: it acts only on a
  // genuine `?item` change, or on a pending (attempted-but-unresolved) param.
  const prevParamRef = useRef<string | null>(null);
  useEffect(() => {
    if (items.length === 0) return; // wait for the first load so a `?item` key can resolve
    readyRef.current = true;
    const changed = itemParam !== prevParamRef.current;
    prevParamRef.current = itemParam;
    if (changed || pendingKeyRef.current) openFromParam(itemParam);
  }, [items.length, itemParam, openFromParam]);

  // panel → URL: mirror the open item's key (replace, so it never floods the history stack).
  useEffect(() => {
    if (!readyRef.current) return; // let the initial URL win the first reconcile
    const sel = panel.selectedId;
    const desiredKey = sel ? byId.get(sel)?.key ?? null : null;
    // Bridge the render gap: right after openFromParam, `sel` hasn't flushed yet — don't strip the
    // param we just opened from before the selection reflects it.
    if (!desiredKey && expectedKeyRef.current && itemParam && itemParam.toLowerCase() === expectedKeyRef.current) return;
    if (desiredKey && expectedKeyRef.current === desiredKey.toLowerCase()) expectedKeyRef.current = null;
    // A queued scroll target only stays valid while its open is pending (expectedKeyRef set) or the
    // selection has landed on it. Once selection settles on a DIFFERENT item with no open in flight,
    // abandon the stale target so a later plain click on it can't trigger an unwanted centering scroll.
    if (!expectedKeyRef.current && scrollTargetRef.current && scrollTargetRef.current !== sel) scrollTargetRef.current = null;
    if ((itemParam ?? null) !== (desiredKey ?? null)) {
      // Never strip a `?item` that names an item not in THIS board's items yet — it's mid-reconcile after
      // a back/forward onto a reused view; the reconcile opens it once byKey gains the key.
      if (!desiredKey && itemParam && !byKey.has(itemParam.toLowerCase())) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (desiredKey) next.set(ITEM_PARAM, desiredKey);
          else next.delete(ITEM_PARAM);
          return next;
        },
        { replace: true },
      );
    }
  }, [panel.selectedId, byId, byKey, itemParam, setParams]);

  // Center a link-opened item once its row/card has painted — retry across frames while the panel
  // opens, ancestors expand and the docked layout settles. Clicks never set a scroll target.
  useEffect(() => {
    const target = scrollTargetRef.current;
    if (!target || panel.selectedId !== target) return;
    let raf = 0;
    let tries = 0;
    const run = () => {
      tries += 1;
      const el = document.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(target)}"]`);
      if (el) {
        scrollTargetRef.current = null;
        if (customScroll) customScroll(target, el);
        else el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
        return;
      }
      if (tries < 40) raf = requestAnimationFrame(run);
      else scrollTargetRef.current = null;
    };
    raf = requestAnimationFrame(run);
    return () => cancelAnimationFrame(raf);
  }, [panel.selectedId, customScroll]);
}
